import { Reveal, Abide } from 'foundation-sites'
import $ from 'jquery'

/**
 * @typedef {Object} DialogOptions
 * @property {string} title
 * @property {string} message
 * @property {string} html
 */

/**
 * @typedef {Object} DialogHandlers
 * @property {BooleanCallback} ok
 * @property {Callback} cancel
 * @property {VoidCallback} common
 */

/**
 * @typedef {Function} BooleanCallback
 * @param {boolean} status
 * @param {FormData} data
 */

/**
 * @typedef {Function} Callback
 * @param {FormData} data
 */

/**
 * @typedef {Function} VoidCallback
 */

class Dialog {
  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<boolean>}
   */
  static async confirm(...args) {
    let plugin = window.__dialogPlugin
    if(!plugin) {
      plugin = new Dialog();
      window.__dialogPlugin = plugin
    }
    return plugin.confirm(...args)
  }

  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<FormData>}
   */
  static async form(...args) {
    let plugin = window.__dialogPlugin
    if(!plugin) {
      plugin = new Dialog();
      window.__dialogPlugin = plugin
    }
    return plugin.form(...args)
  }

  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<void>}
   */
  static async alert(...args) {
    let plugin = window.__dialogPlugin
    if(!plugin) {
      plugin = new Dialog();
      window.__dialogPlugin = plugin
    }
    return plugin.alert(...args)
  }

  /**
   * @type {HTMLDivElement}
   */
  #dialog = null

  /**
   * @type {Reveal}
   */
  #reveal = null

  /**
   * @type {DialogHandlers}
   */
  #handlers = null

  /**
   * @type {boolean}
   */
  #isOpen = false

  /**
   * @type {[string, DialogOptions, Function][]}
   */
  #queue = []

  constructor() {
    this.#handlers = {
      common: [],
      ok: [],
      cancel: []
    }

    this.#dialog = document.createElement('div')
    this.#dialog.innerHTML = `
      <h5></h5>
      <button class="close-button" data-close aria-label="Close modal" type="button">
        <span aria-hidden="true">&times;</span>
      </button>
      <div class="body">
      </div>
      <div class="footer text-right">
        <button type="button" class="button margin-bottom-0 secondary hollow">Annulla</button>
        <button type="button" class="button margin-bottom-0 primary">Ok</button>
      </div>
    `
    this.#dialog.classList.add('alert-dialog')
    this.#dialog.classList.add('reveal')
    this.#dialog.dataset.reveal = true
    this.#dialog.dataset.closeOnClock = false

    this.#reveal = new Reveal($(this.#dialog), {multipleOpened: true})

    this.#dialog.querySelector('.footer .button.primary').addEventListener('click', (ev)=>{
      ev.preventDefault();
      this.#ok();
    })
    this.#dialog.querySelector('.footer .button.secondary').addEventListener('click', (ev)=>{
      ev.preventDefault();
      this.#cancel();
    })

    this.#dialog.addEventListener('open.zf.reveal', (ev)=>{
      this.#isOpen = true;
    })

    this.#dialog.addEventListener('closed.zf.reveal', (ev)=>{
      this.#isOpen = true;      
      ev.preventDefault();
      this.#cancel();
      this.#next();
    })
  }

  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<boolean>}
   */

  async confirm(options) {
    return new Promise((resolve)=> {
      this.#renderConfirm(options, resolve)
    })
  }

  #renderConfirm(options, resolve) {
    if(this.#isOpen) {
      this.#queue.push(['confirm', options, resolve])
      return
    }
    
    this.#render(options)

    this.#dialog.querySelector('button.secondary').classList.remove('hide')
    this.#dialog.querySelector('button.secondary').parentNode.classList.remove('text-center')
    
    this.#reveal.open()

    this.ok(()=> resolve(true))
    this.cancel(()=> resolve(false))    
  }

  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<boolean>}
   */

  async form(options) {
    return new Promise((resolve)=> {
      this.#renderForm(options, resolve)
    })
  }

  #renderForm(options, resolve) {
    if(this.#isOpen) {
      this.#queue.push(['form', options, resolve])
      return
    }
    
    this.#render(options)

    this.#dialog.querySelector('button.secondary').classList.remove('hide')
    this.#dialog.querySelector('button.secondary').parentNode.classList.remove('text-center')
    
    this.#reveal.open()

    this.ok((data)=> resolve(data))
    this.cancel(()=> resolve(false))
  }

  /**
   * 
   * @param {DialogOptions} options 
   * @returns {Promise<void>}
   */

  async alert(options) {
    return new Promise((resolve)=> {
      this.#renderAlert(options, resolve)
    })
  }

  #renderAlert(options, resolve) {
    if(this.#isOpen) {
      this.#queue.push(['alert', options, resolve])
      return
    }
    
    this.#render(options)

    this.#dialog.querySelector('button.secondary').classList.add('hide')
    this.#dialog.querySelector('button.secondary').parentNode.classList.remove('text-center')
    
    this.#reveal.open()
  }

  /**
   *
   * @param {BooleanCallback} cb 
   * @returns 
   */

  on(cb) {
    if(typeof cb == 'function') this.#handlers.common.push(cb);
    return this;
  }

  /**
   *
   * @param {Callback} cb 
   * @returns 
   */

  ok(cb) {
    if(typeof cb == 'function') this.#handlers.ok.push(cb);
    return this;
  }

  /**
   *
   * @param {VoidCallback} cb 
   * @returns 
   */

  cancel(cb) {
    if(typeof cb == 'function') this.#handlers.cancel.push(cb);
    return this;
  }

  #render({title = null, message, html, ...rest}) {
    title = title || 'Attenzione!'
    this.#dialog.querySelector('h5').innerText = title
    if(message && !html)
      this.#dialog.querySelector('.body').innerHTML = this.#simpleFormat(message)
    if(html)
      this.#dialog.querySelector('.body').innerHTML = html
    $(this.#dialog).foundation()
  }

  #simpleFormat(text) {
    if(text) {
      let paragraph = [[]]
      text.split(/(?:\r\n|\r|\n)/).forEach((line)=> {
        if(line.trim() == '') {
          paragraph.push([])
        } else {
          paragraph[paragraph.length-1].push(line.trim())
        }
      })

      paragraph = paragraph.map((p)=> p.join('<br>'))

      return `<p>${paragraph.join('</p><p>')}</p>`
    }
    return text;
  }

  #clearHandlers() {
    this.#handlers = {
      common: [],
      ok: [],
      cancel: []
    }
  }

  async #ok() {
    let data = null
    const form = this.#dialog.querySelector('form')
    if(form) {
      const abide = new Abide($(form))
      if(!abide.validateForm()) return
      data = new FormData(form)
    }

    this.#handlers.ok.forEach((cb)=> cb(data))
    this.#handlers.common.forEach((cb)=> cb(true, data))
    this.#reveal.close()
    this.#clearHandlers()
  }

  #cancel() {
    this.#handlers.cancel.forEach((cb)=> cb())
    this.#handlers.common.forEach((cb)=> cb(false))
    this.#reveal.close()
    this.#clearHandlers()
  }

  #next() {
    if(this.#queue.length > 0) {
      const [type, options, resolve] = this.#queue.shift()
      if(type == 'confirm') {
        this.#renderConfirm(options, resolve)
      } else if(type == 'form') {
        this.#renderForm(options, resolve)
      } else if(type == 'alert') {
        this.#renderAlert(options, resolve)
      }
    }
  }
}

export default Dialog
